import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { Toast } from 'components/shared/Toaster';
import ConfirmationDialog from 'components/ConfirmationDialog';
import JsonEditor from 'components/shared/JsonEditor/JsonEditor';
import { deleteClientPlaidConfig, getClientPlaidConfig, saveClientPlaidConfig } from 'actions/client';
import strings from 'strings';
import styles from '../profile.module.scss';

const t = strings.PLAID;

// Placeholders are the defaults the page shows when a field is left empty.
const FIELDS = [
    { name: 'brandName', label: 'Brand name', placeholder: 'Roxwrite', helperText: 'Logo alt text and the "To learn more about ..." line' },
    { name: 'title', label: 'Title', placeholder: t.TITLE },
    { name: 'subTitle', label: 'Subtitle', placeholder: t.SUB_TITLE },
    { name: 'brief', label: 'Description', placeholder: t.BRIEF, multiline: true },
    { name: 'privacyLinkText', label: 'Privacy link text', placeholder: t.TEXT_LINK_ROXWRITE },
    { name: 'privacyLinkUrl', label: 'Privacy link URL', placeholder: 'https://roxwrite-new-site.webflow.io/privacy-policy' },
    { name: 'checkboxLabel', label: 'Checkbox label', placeholder: 'I accept the Terms and Conditions' },
    { name: 'authorizeButtonLabel', label: 'Button label', placeholder: t.AUTHORIZE },
];
const KEYS = ['logoBase64', ...FIELDS.map((f) => f.name)];
const LOGO_DATA_URL = /^data:image\/[\w.+-]+;base64,[A-Za-z0-9+/=]+$/;
const HTTP_URL = /^https?:\/\/.+$/;
const EMPTY = KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

// Form shape (every key a string) <-> API/JSON shape (only filled keys, no ids, so it is portable between environments).
const toForm = (config) => KEYS.reduce((acc, key) => ({ ...acc, [key]: config?.[key] || '' }), {});
const toPayload = (form) => KEYS.reduce((acc, key) => (form[key] ? { ...acc, [key]: form[key] } : acc), {});

export const validateImportedConfig = (json) => {
    const errors = [];
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
        return ['The file must contain a JSON object'];
    }
    const unknown = Object.keys(json).filter((key) => !KEYS.includes(key));
    if (unknown.length) errors.push(`Unknown fields: ${unknown.join(', ')}`);
    KEYS.filter((key) => json[key] != null && typeof json[key] !== 'string')
        .forEach((key) => errors.push(`"${key}" must be a string`));
    if (json.logoBase64 && !LOGO_DATA_URL.test(json.logoBase64)) errors.push('"logoBase64" must be a base64 image data URL');
    if (json.privacyLinkUrl && !HTTP_URL.test(json.privacyLinkUrl)) errors.push('"privacyLinkUrl" must be an http(s) URL');
    FIELDS.filter((f) => !f.multiline && typeof json[f.name] === 'string' && json[f.name].length > 255)
        .forEach((f) => errors.push(`"${f.name}" must be at most 255 characters`));
    return errors;
};

/**
 * Branding and texts of this client's Plaid quickstart page, editable as a form or as JSON (same config, so the
 * JSON can be copied between environments). Rox-admin only (admin/client-plaid-config endpoints).
 */
const ClientPlaidConfigTab = ({ clientUuid, clientName }) => {
    const [form, setForm] = useState(EMPTY);
    const [saved, setSaved] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [jsonMode, setJsonMode] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState('');
    const logoInputRef = useRef(null);

    const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
    const hasSavedConfig = Object.keys(toPayload(saved)).length > 0;

    const fetchConfig = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        getClientPlaidConfig(clientUuid)
            .then((res) => {
                const loaded = toForm(res);
                setForm(loaded);
                setSaved(loaded);
            })
            .catch(() => setLoadFailed(true))
            .finally(() => setLoading(false));
    }, [clientUuid]);

    useEffect(fetchConfig, [fetchConfig]);

    const setField = (name) => (e) => {
        const { value } = e.target; // read before React 16 recycles the synthetic event
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const save = (payload) => {
        setSaving(true);
        return saveClientPlaidConfig(clientUuid, payload)
            .then((res) => {
                const next = toForm(res);
                setForm(next);
                setSaved(next);
                Toast({ type: 'success', message: `Plaid page configuration saved for ${clientName || 'this client'}.` });
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        save(toPayload(form));
    };

    const handleReset = () => {
        setConfirmReset(false);
        setSaving(true);
        deleteClientPlaidConfig(clientUuid)
            .then(() => {
                setForm(EMPTY);
                setSaved(EMPTY);
                Toast({ type: 'success', message: 'Plaid page restored to the default Roxwrite page.' });
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    const handleLogoChange = (e) => {
        const file = e?.target?.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            Toast({ type: 'error', message: 'Please select an image file' });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            Toast({ type: 'error', message: 'File size must be less than 2MB' });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setForm((prev) => ({ ...prev, logoBase64: reader.result }));
        reader.onerror = () => Toast({ type: 'error', message: 'Failed to read file' });
        reader.readAsDataURL(file);
    };

    const openJsonEditor = () => {
        setJsonText(JSON.stringify(toPayload(form), null, 2));
        setJsonError('');
        setJsonMode(true);
    };

    const applyJson = () => {
        let json;
        try {
            json = JSON.parse(jsonText);
        } catch (err) {
            setJsonError(`Invalid JSON: ${err.message}`);
            return;
        }
        const errors = validateImportedConfig(json);
        if (errors.length) {
            setJsonError(errors.join('. '));
            return;
        }
        setForm(toForm(json));
        setJsonMode(false);
        Toast({ type: 'info', message: 'JSON applied to the form. Click Save to persist it.' });
    };

    if (loading) {
        return (
            <Card>
                <div className="text-center p-4"><CircularProgress /></div>
            </Card>
        );
    }

    if (loadFailed) {
        return (
            <Card>
                <div className={`${styles.profileContainer} text-center`}>
                    <p>Could not load the Plaid page configuration.</p>
                    <Button variant="outlined" color="primary" onClick={fetchConfig}>Retry</Button>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                    <div>
                        <h5>Plaid Page</h5>
                        <Typography variant="body2" color="textSecondary">
                            Branding and texts shown to owners on the Plaid bank connection page. Empty fields keep the default Roxwrite content.
                        </Typography>
                    </div>
                    <Box display="flex" gridGap={8} alignItems="flex-start">
                        {jsonMode ? (
                            <>
                                <Button variant="outlined" onClick={() => setJsonMode(false)} disabled={saving}>Cancel</Button>
                                <Button variant="contained" color="primary" onClick={applyJson} disabled={saving}>Apply</Button>
                            </>
                        ) : (
                            <Button variant="outlined" color="primary" onClick={openJsonEditor} disabled={saving}>Edit JSON</Button>
                        )}
                    </Box>
                </div>

                <form onSubmit={handleSubmit}>
                    {jsonMode ? (
                        <Box my={2}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Edit, or paste the JSON copied from another environment, then click Apply to load it into the form.
                            </Typography>
                            <JsonEditor
                                value={jsonText}
                                onChange={(text) => { setJsonText(text); setJsonError(''); }}
                                hasError={!!jsonError}
                                error={jsonError}
                            />
                        </Box>
                    ) : (
                    <>
                    <Box display="flex" alignItems="center" gridGap={16} my={2}>
                        {form.logoBase64 ? (
                            <img src={form.logoBase64} alt={form.brandName || 'Logo'} style={{ maxWidth: 166, maxHeight: 63, objectFit: 'contain' }} />
                        ) : (
                            <Typography variant="body2" color="textSecondary">Default Roxwrite logo</Typography>
                        )}
                        <Button variant="outlined" color="primary" onClick={() => logoInputRef.current?.click()}>
                            {form.logoBase64 ? 'Change Logo' : 'Upload Logo'}
                        </Button>
                        {form.logoBase64 && (
                            <Button color="secondary" onClick={() => setForm((prev) => ({ ...prev, logoBase64: '' }))}>Remove Logo</Button>
                        )}
                        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                    </Box>

                    {FIELDS.map((field) => (
                        <TextField
                            key={field.name}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            label={field.label}
                            placeholder={field.placeholder}
                            helperText={field.helperText}
                            value={form[field.name]}
                            onChange={setField(field.name)}
                            multiline={field.multiline}
                            rows={field.multiline ? 5 : undefined}
                            inputProps={{ maxLength: field.multiline ? undefined : 255 }}
                            InputLabelProps={{ shrink: true }}
                        />
                    ))}
                    </>
                    )}

                    <Box display="flex" justifyContent="flex-end" gridGap={8} mt={2}>
                        {hasSavedConfig && (
                            <Button variant="outlined" color="secondary" onClick={() => setConfirmReset(true)} disabled={saving}>
                                Restore Default Page
                            </Button>
                        )}
                        <Button type="submit" variant="contained" color="primary" disabled={!isDirty || saving}>
                            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
                        </Button>
                    </Box>
                </form>
            </div>

            <ConfirmationDialog
                open={confirmReset}
                onClose={() => setConfirmReset(false)}
                onConfirm={handleReset}
                title="Restore Default Page"
                message={`Remove the custom Plaid page configuration of "${clientName || 'this client'}"? Owners will see the default Roxwrite page again.`}
                loading={saving}
                confirmText="Restore"
            />
        </Card>
    );
};

export default ClientPlaidConfigTab;
